import { z } from 'zod';

/**
 * Plesk import (G8): the API contract for the read-only inventory module.
 * Phase 0 records what the Plesk server holds — versioned snapshots of
 * resellers, customers, plans, subscriptions, domains, mail, databases,
 * DNS, certificates, cron jobs, extensions and deployment/runtime evidence
 * — and derives a migration-readiness report. Nothing here writes to Plesk.
 */

// ── Connection settings ─────────────────────────────────────────────

export const pleskSshSettingsSchema = z.object({
  enabled: z.boolean().default(false),
  /** Defaults to the panel connection host when omitted. */
  host: z.string().trim().min(1).optional(),
  port: z.number().int().min(1).max(65535).default(22),
  username: z.string().trim().min(1).default('root'),
  /** Private key (PEM). Omit to keep the currently stored key. */
  privateKey: z.string().trim().min(1).optional(),
});

export const pleskSettingsSchema = z.object({
  /** Hostname or IP of the Plesk panel — no scheme, no port. */
  host: z
    .string()
    .trim()
    .min(1)
    .regex(/^[a-zA-Z0-9.\-:[\]]+$/, 'hostname or IP address, without scheme or path'),
  port: z.number().int().min(1).max(65535).default(8443),
  /** Plesk administrator login (XML API and REST share it). */
  login: z.string().trim().min(1),
  /** Plesk password. Omit to keep the currently stored one. */
  password: z.string().min(1).optional(),
  /**
   * Self-signed panel certificates are common. The right fix is pinning
   * the panel's CA via `caCertificate`; disabling verification is the
   * documented last resort (MITM exposure — see docs/plesk-import.md).
   */
  verifyTls: z.boolean().default(true),
  /** PEM CA certificate to verify the panel's TLS cert against. */
  caCertificate: z.string().trim().min(1).nullish(),
  /**
   * Optional SSH access for fixed, reviewed evidence collectors: cron jobs,
   * database sizes, Git deployments and runtime/toolkit availability/use.
   */
  ssh: pleskSshSettingsSchema.optional(),
});
export type PleskSettingsInput = z.infer<typeof pleskSettingsSchema>;

export interface PleskSshSettingsDto {
  enabled: boolean;
  host: string | null;
  port: number;
  username: string;
  hasKey: boolean;
}

export interface PleskSettingsDto {
  configured: boolean;
  host: string | null;
  port: number;
  login: string | null;
  verifyTls: boolean;
  caCertificate: string | null;
  hasPassword: boolean;
  ssh: PleskSshSettingsDto;
}

export interface PleskTestResultDto {
  xmlApi: { ok: boolean; error: string | null };
  restApi: { ok: boolean; version: string | null; error: string | null };
  ssh: { ok: boolean; error: string | null } | null;
}

// ── Inventory items ─────────────────────────────────────────────────

export const PLESK_ITEM_KINDS = [
  'reseller',
  'customer',
  'service_plan',
  'subscription',
  'domain',
  'mail_domain',
  'mailbox',
  'database',
  'dns_zone',
  'certificate',
  'cron_job',
  'extension',
  'git_repository',
  'runtime',
  'marketplace_usage',
] as const;
export type PleskItemKind = (typeof PLESK_ITEM_KINDS)[number];

export interface PleskResellerPayload {
  pleskId: number;
  login: string;
  name: string;
  email: string | null;
  status: string;
}

export interface PleskCustomerPayload {
  pleskId: number;
  login: string;
  name: string;
  company: string | null;
  email: string | null;
  phone: string | null;
  status: string;
  created: string | null;
  /** Owning reseller login; null when owned by the administrator. */
  resellerLogin: string | null;
}

export interface PleskServicePlanPayload {
  pleskId: number;
  name: string;
  /** Raw Plesk limit name → value pairs, resolved by the capability registry. */
  limits: Record<string, string>;
  /** Raw Plesk permission name → value pairs, resolved by the capability registry. */
  permissions: Record<string, string>;
}

export interface PleskDiskUsage {
  totalBytes: number | null;
  webBytes: number | null;
  mailBytes: number | null;
  databaseBytes: number | null;
  logsBytes: number | null;
}

export interface PleskSubscriptionPayload {
  pleskId: number;
  /** The main domain name — Plesk's identity for a webspace. */
  name: string;
  /** Owner login (customer or reseller); null when admin-owned. */
  ownerLogin: string | null;
  /** Owner id — the reliable link (18.0.78 omits owner-login for customers). */
  ownerId: number | null;
  planName: string | null;
  status: string;
  created: string | null;
  ipAddresses: string[];
  /** System user of the webspace (also the FTP login). */
  sysUser: string | null;
  /** vrt_hst (virtual hosting), std_fwd / frm_fwd (forwarding), none. */
  hostingType: string;
  phpVersion: string | null;
  phpHandlerId: string | null;
  phpHandlerType: string | null;
  docroot: string | null;
  /** Raw effective limit name → value pairs. */
  limits: Record<string, string>;
  /** Raw permission name → value pairs. */
  permissions: Record<string, string>;
  /** Recorded verbatim for G9's mapping work. */
  limitDescriptor: unknown;
  permissionDescriptor: unknown;
  diskUsage: PleskDiskUsage;
}

export interface PleskDomainPayload {
  pleskId: number;
  name: string;
  domainType: 'site' | 'subdomain' | 'alias';
  /**
   * Plesk XML object views that contributed to this topology row. Plesk can
   * expose the same domain id/name through both `site get` and
   * `subdomain get`; retaining both observations avoids either duplicating a
   * destination hostname or losing the subdomain relationship.
   */
  sourceObjectTypes?: ('webspace' | 'site' | 'subdomain' | 'site-alias')[];
  subscriptionPleskId: number;
  /** Parent site name for subdomains and aliases. */
  parentDomain: string | null;
  hostingType: string | null;
  phpVersion: string | null;
  phpHandlerId: string | null;
  docroot: string | null;
  sslEnabled: boolean | null;
  certificateName: string | null;
  status: string | null;
  /**
   * Safe, portable hosting intent only. Older frozen snapshots predate this
   * field, so migration planning must tolerate it being absent.
   */
  hostingSettings?: PleskPortableHostingSettings;
  /** Standard/frame forwarding destination, when this is redirect hosting. */
  forwarding?: PleskForwardingPayload | null;
  /** Web/mail/SEO behavior for a Plesk domain alias. */
  aliasSettings?: PleskDomainAliasSettings | null;
  /**
   * Protected path names and access locations. Authentication users and
   * passwords are deliberately never requested or stored.
   */
  protectedPaths?: PleskProtectedPathPayload[];
}

export interface PleskForwardingPayload {
  mode: 'standard' | 'frame';
  destinationUrl: string | null;
  /** Plesk exposes 301/302 on some versions; null means operator review. */
  httpCode: 301 | 302 | null;
}

export interface PleskDomainAliasSettings {
  webEnabled: boolean | null;
  mailEnabled: boolean | null;
  seoRedirect: boolean | null;
  manageDns: boolean | null;
  status: string | null;
}

export interface PleskProtectedPathPayload {
  pleskId: number;
  path: string;
  header: string | null;
  ssl: boolean | null;
  nonSsl: boolean | null;
  cgi: boolean | null;
}

export interface PleskPortableHostingSettings {
  /** Plesk's HTTP→HTTPS intent; Coolify/Traefik is the replacement owner. */
  httpsRedirect: boolean | null;
  /** Plesk SEO preference for the canonical www/non-www hostname. */
  canonicalHost: 'www' | 'non-www' | 'none' | 'unknown';
  /** A disabled/suspended site is retained as maintenance intent. */
  maintenanceMode: boolean;
  customErrorDocuments: boolean | null;
  phpEnabled: boolean | null;
  /**
   * Fixed allowlist of portable php.ini values. Arbitrary additional
   * directives are represented only by the boolean below because they can
   * contain paths or customer secrets.
   */
  phpSettings: Record<string, string>;
  customPhpDirectivesPresent: boolean;
  web: {
    cgi: boolean | null;
    fastcgi: boolean | null;
    perl: boolean | null;
    python: boolean | null;
    ssi: boolean | null;
    webstat: string | null;
    cgiMode: string | null;
    cacheSeconds: number | null;
  };
  /**
   * Source-specific flags are evidence for a reviewed replacement, never
   * fake Coolify toggles.
   */
  reviewedReplacements: { sourceKey: string; sourceValue: string; replacement: string }[];
}

export interface PleskMailboxPayload {
  /** Full address (user@domain). */
  name: string;
  domain: string;
  subscriptionPleskId: number;
  mailboxEnabled: boolean;
  /** Plesk quota; null when unlimited or unknown. */
  quotaBytes: number | null;
  /** Actual mailbox size from the mailbox-usage dataset; null when unknown. */
  usedBytes: number | null;
  aliases: string[];
  forwardingEnabled: boolean;
  forwardingAddresses: string[];
  autoresponderEnabled: boolean;
  /** Full automatic-response settings; these are configuration, never mailbox message content. */
  autoresponders: PleskAutoresponderPayload[];
  /** Plesk antivirus direction (`mailname/antivir`); no scan data is collected. */
  virusFilter: PleskVirusFilterMode;
  /** Optional fixed-command SpamAssassin inventory from the source host. */
  spamFilter: PleskSpamFilterPayload;
}

export type PleskVirusFilterMode =
  | 'off'
  | 'incoming'
  | 'outgoing'
  | 'incoming_outgoing'
  | 'unknown';

export interface PleskAutoresponderPayload {
  enabled: boolean;
  /** Optional on historical snapshots; missing evidence must not imply a default. */
  replyFrequency?: PleskReplyFrequency;
  subject: string | null;
  /** Optional incoming-subject trigger configured by the operator. */
  requestText: string | null;
  contentType: string | null;
  charset: string | null;
  /** Configured response template, not a message read from the mailbox. */
  responseText: string | null;
  forwardAddress: string | null;
  /** ISO date when parseable, otherwise the source value for an explicit disposition. */
  endDate: string | null;
  /** Attachment names only; attachment bytes are deliberately never collected. */
  attachmentNames: string[];
}

export interface PleskReplyFrequency {
  collectionStatus: 'collected' | 'not-collected' | 'unavailable';
  repliesPerSenderPerDay: number | null;
  /** Source tracking capacity is evidence, not a destination Sieve setting. */
  trackedSenderLimit: number | null;
}

export type PleskSpamCollectionStatus =
  | 'collected'
  | 'not-collected'
  | 'unavailable'
  | 'failed';

export interface PleskSpamFilterPayload {
  collectionStatus: PleskSpamCollectionStatus;
  enabled: boolean | null;
  useServerSettings: boolean | null;
  scoreThreshold: number | null;
  action: 'delete' | 'mark' | 'move' | 'none' | 'unknown';
  modifySubject: boolean | null;
  subjectPrefix: string | null;
  rejectSpam: boolean | null;
  whiteList: string[];
  blackList: string[];
  error: string | null;
}

export interface PleskMailDomainPayload {
  siteId: number;
  domain: string;
  subscriptionPleskId: number;
  mailServiceEnabled: boolean | null;
  /** Plesk handling for mail addressed to a missing local recipient. */
  nonexistentUser:
    | { mode: 'reject' }
    | { mode: 'bounce'; message: string | null }
    | { mode: 'forward'; address: string | null }
    | { mode: 'unknown'; value: string | null };
  webmail: string | null;
  certificateName: string | null;
  greylistingEnabled: boolean | null;
  spamProtectionSigningEnabled: boolean | null;
}

export interface PleskDatabasePayload {
  pleskId: number;
  name: string;
  /** Plesk database type: mysql, postgresql, mssql. */
  engine: string;
  subscriptionPleskId: number;
  serverHost: string | null;
  /** Filled by the optional SSH collector; null when not collected. */
  sizeBytes: number | null;
}

export interface PleskDnsRecord {
  /** Stable Plesk database identity returned by dns.get_rec. */
  pleskId: number;
  /** The source owner identity exactly as returned by Plesk. */
  siteId: number | null;
  siteAliasId: number | null;
  type: string;
  host: string;
  value: string;
  opt: string | null;
  /** Per-record TTL override; null inherits the zone TTL only after collection is verified. */
  ttl: number | null;
  /**
   * Every primitive field returned inside the dns.get_rec data element.
   * Named fields above are conveniences; this lossless bag prevents a newer
   * Plesk record attribute from disappearing from the frozen snapshot.
   */
  fields: Record<string, unknown>;
}

export interface PleskDnsSoa {
  ttl: number | null;
  refresh: number | null;
  retry: number | null;
  expire: number | null;
  minimum: number | null;
}

export type PleskDnssecCollectionStatus = 'collected' | 'not-collected' | 'unavailable' | 'failed';

export interface PleskDnssecState {
  collectionStatus: PleskDnssecCollectionStatus;
  /** null means the read surface could not establish signing state. */
  enabled: boolean | null;
  /** Registrar-ready public DS values only; private key material is never collected. */
  dsRecords: string[];
  /** Public, non-secret key/policy fields printed by `dnssec info`. */
  settings: Record<string, string>;
  error: string | null;
}

export interface PleskDnsZonePayload {
  /** Site/webspace id that owns the Plesk zone. */
  siteId: number;
  domain: string;
  subscriptionPleskId: number;
  zoneType: 'master' | 'slave';
  enabled: boolean;
  soa: PleskDnsSoa;
  /** Populated for secondary zones through dns.get_master_server. */
  masterServers: string[];
  dnssec: PleskDnssecState;
  records: PleskDnsRecord[];
  /** Absent on historical snapshots; DNS import and equality require a fresh verified TTL read. */
  recordTtlCollectionStatus?: 'collected' | 'not-collected' | 'failed';
}

export interface PleskCertificatePayload {
  name: string;
  subscriptionPleskId: number;
  usedByDomains: string[];
  /** Heuristic on Plesk's own naming; drives the custom-cert flag. */
  isLetsEncrypt: boolean;
}

export interface PleskCronJobPayload {
  subscriptionPleskId: number;
  sysUser: string;
  /** The five crontab schedule fields, verbatim. */
  schedule: string;
  /**
   * Credential-redacted command text. Commands with secret-shaped arguments
   * retain enough structure for operator review but never retain the value.
   */
  command: string;
  requiresReview?: boolean;
  reviewReasons?: string[];
}

export type PleskExtensionUsageStatus = 'used' | 'not-detected' | 'unknown';

export interface PleskExtensionPayload {
  id: string;
  name: string;
  version: string | null;
  release: number | null;
  active: boolean;
  /** Installed is fact; usage remains unknown unless a fixed collector can prove it. */
  usageStatus: PleskExtensionUsageStatus;
  /** Bounded, secret-free evidence labels (for example "2 Git repositories"). */
  usageEvidence: string[];
}

export type PleskMarketplaceProduct = 'softaculous' | 'sitejet' | 'xovi';

/**
 * Frozen evidence from a fixed, read-only marketplace-product collector.
 * Sitejet/XOVI rows exist independently of installed-extension inventory so
 * stale domain markers cannot disappear merely because an extension was
 * removed before the snapshot.
 */
export interface PleskMarketplaceUsagePayload {
  product: PleskMarketplaceProduct;
  collectionStatus: 'collected' | 'not-collected' | 'unreconciled';
  activeObjectCount: number | null;
  domains: string[];
  /** Plesk service-plan ids reached through domain -> subscription -> plan. */
  planExternalIds: string[];
  /** Marker domains absent from the frozen domain inventory. */
  unmatchedDomains: string[];
  /** Known domains whose subscription or replacement-bearing plan was unresolved. */
  uncoveredDomains: string[];
}

export interface PleskGitRemoteIdentity {
  /** Host (and non-default port when present), never a username/password. */
  host: string;
  /** Repository path without query/fragment credentials. */
  path: string;
  /** Transport is retained because it is safe and helps select Coolify auth. */
  transport: 'https' | 'ssh' | 'git' | 'other';
  /**
   * `configured` means the source URL carried a username or used SSH. The
   * username itself is discarded.
   */
  authentication: 'none' | 'configured' | 'unknown';
  /** Reconstructed URL containing repository identity only, never credentials. */
  credentialFreeUrl: string;
}

export interface PleskGitRepositoryPayload {
  name: string;
  domain: string;
  subscriptionPleskId: number | null;
  repositoryType: 'pull' | 'push' | 'unknown';
  remote: PleskGitRemoteIdentity | null;
  activeBranch: string | null;
  deploymentMode: 'auto' | 'manual' | 'none' | 'unknown';
  deploymentPath: string | null;
  /** Whether actions exist; action bodies are deliberately never collected. */
  postDeployActionsConfigured: boolean | null;
  /**
   * Immutable migration policy for source hooks. Inventory records only the
   * presence of actions; it never reads bodies, runs them, or changes them.
   */
  postDeployActionPolicy?: {
    disposition: 'none' | 'operator-review' | 'unknown';
    commandBodiesCollected: false;
    inventoryExecution: 'never';
    sourceMutation: 'never';
  };
  skipSslVerification: boolean | null;
}

export interface PleskRuntimeUse {
  domain: string;
  version: string | null;
}

export interface PleskRuntimeSummaryPayload {
  toolkit: 'php' | 'nodejs' | 'wp-toolkit';
  collectionStatus: 'collected' | 'not-collected';
  availableVersions: string[];
  usedInstances: number | null;
  /** Node/PHP use evidence only; WP summary intentionally retains a count, not site secrets. */
  uses: PleskRuntimeUse[];
}

export interface PleskInventoryItemDto {
  id: string;
  kind: PleskItemKind;
  /** Plesk-side identity (numeric id, or a synthesized stable key). */
  pleskId: string;
  name: string;
  payload: unknown;
}

export const pleskItemsQuerySchema = z.object({
  kind: z.enum(PLESK_ITEM_KINDS).optional(),
  /** Case-insensitive substring match on the item name. */
  q: z.string().trim().max(200).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(500).default(100),
});
export type PleskItemsQuery = z.infer<typeof pleskItemsQuerySchema>;

// ── Snapshots ───────────────────────────────────────────────────────

export const PLESK_SNAPSHOT_STATUSES = ['running', 'completed', 'partial', 'failed'] as const;
export type PleskSnapshotStatus = (typeof PLESK_SNAPSHOT_STATUSES)[number];

export interface PleskCollectionWarning {
  /** Collection step that degraded, e.g. `mail`, `dns`, `cron`. */
  scope: string;
  /** The object being collected when it happened (domain, login, …). */
  subject: string | null;
  message: string;
}

export interface PleskSnapshotStats {
  counts: Partial<Record<PleskItemKind, number>>;
  apiCalls: { xml: number; rest: number; ssh: number };
  /** Handler id → PHP version/type, as reported by the php-handler operator. */
  phpHandlers: Record<string, { version: string; type: string }>;
}

export interface PleskSnapshotDto {
  id: string;
  status: PleskSnapshotStatus;
  startedAt: string;
  finishedAt: string | null;
  durationMs: number | null;
  pleskVersion: string | null;
  stats: PleskSnapshotStats | null;
  warnings: PleskCollectionWarning[];
  error: string | null;
}

export interface PleskSnapshotDetailDto extends PleskSnapshotDto {
  report: PleskReadinessReport | null;
}

export interface PleskImportStatusDto {
  configured: boolean;
  sshConfigured: boolean;
  running: boolean;
  lastSnapshot: PleskSnapshotDto | null;
}

// ── Migration-readiness report ──────────────────────────────────────

export const PLESK_FLAG_KEYS = [
  'eol_php',
  'security_only_php',
  'heavy_mail',
  'unsupported_db_engine',
  'dns_hosted_in_plesk',
  'custom_certificate',
  'has_cron_jobs',
  'cron_not_collected',
  'forwarding_hosting',
  'no_hosting',
] as const;
export type PleskFlagKey = (typeof PLESK_FLAG_KEYS)[number];

export type PleskFlagSeverity = 'info' | 'warn' | 'high';

export interface PleskComplexityFlag {
  key: PleskFlagKey;
  severity: PleskFlagSeverity;
  detail: string;
}

/** Display metadata for flags — the report and UI render from this. */
export const PLESK_FLAG_DEFS: Record<
  PleskFlagKey,
  { label: string; severity: PleskFlagSeverity; description: string }
> = {
  eol_php: {
    label: 'EOL PHP',
    severity: 'high',
    description: 'A site runs a PHP version past its end of life — no container base image to match; upgrade during migration.',
  },
  security_only_php: {
    label: 'PHP security-only',
    severity: 'warn',
    description: 'A site runs a PHP version out of active support (security fixes only).',
  },
  heavy_mail: {
    label: 'Heavy mail',
    severity: 'warn',
    description: 'Many mailboxes or large mail storage — plan the mailbox migration window accordingly.',
  },
  unsupported_db_engine: {
    label: 'Unsupported DB engine',
    severity: 'high',
    description: 'A database engine Coolify cannot host (e.g. MSSQL) — needs an external home or a conversion.',
  },
  dns_hosted_in_plesk: {
    label: 'DNS in Plesk',
    severity: 'warn',
    description: 'Plesk is the authoritative DNS for a zone — records must move to the panel DNS module before cutover.',
  },
  custom_certificate: {
    label: 'Custom certificate',
    severity: 'warn',
    description: "A certificate that isn't Let's Encrypt (purchased/wildcard) — export and re-install manually; Coolify only auto-issues LE.",
  },
  has_cron_jobs: {
    label: 'Cron jobs',
    severity: 'info',
    description: 'Scheduled tasks exist — recreate them as Coolify scheduled tasks or in-container cron.',
  },
  cron_not_collected: {
    label: 'Cron unknown',
    severity: 'info',
    description: 'SSH collection is off, so cron jobs were not enumerated — verify manually before cutover.',
  },
  forwarding_hosting: {
    label: 'Forwarding',
    severity: 'info',
    description: 'Domain is a redirect (standard/frame forwarding) — recreate as a static redirect app.',
  },
  no_hosting: {
    label: 'No hosting',
    severity: 'info',
    description: 'Subscription has no web hosting (mail/DNS only) — nothing to containerize, but mail/DNS still move.',
  },
};

export interface PleskSubscriptionReadiness {
  pleskId: number;
  name: string;
  customerLogin: string | null;
  customerName: string | null;
  planName: string | null;
  status: string;
  domains: number;
  phpVersions: string[];
  mailboxes: number;
  mailUsageBytes: number | null;
  /** Engine → count. */
  databases: Record<string, number>;
  databaseUsageBytes: number | null;
  dnsZonesHosted: number;
  certificates: number;
  customCertificates: number;
  /** null when the SSH collector was not enabled. */
  cronJobs: number | null;
  diskUsageBytes: number | null;
  flags: PleskComplexityFlag[];
}

export interface PleskReadinessReport {
  generatedAt: string;
  totals: {
    resellers: number;
    customers: number;
    servicePlans: number;
    subscriptions: number;
    domains: number;
    mailboxes: number;
    databases: number;
    dnsZonesHosted: number;
    certificates: number;
    cronJobs: number | null;
    diskUsageBytes: number | null;
    mailUsageBytes: number | null;
    databaseUsageBytes: number | null;
  };
  flagTotals: Partial<Record<PleskFlagKey, number>>;
  subscriptions: PleskSubscriptionReadiness[];
}

// ── PHP support lifecycle ───────────────────────────────────────────

/**
 * PHP minor version → [active support end, security support end].
 * Sources: php.net/supported-versions (recorded 2026-07). Data-driven so
 * the readiness report stays honest as time passes: flags are computed
 * against the snapshot date, not against a hardcoded version list.
 */
export const PHP_SUPPORT_WINDOWS: Record<string, [activeEnd: string, securityEnd: string]> = {
  '5.2': ['2010-06-24', '2011-01-06'],
  '5.3': ['2013-07-11', '2014-08-14'],
  '5.4': ['2014-09-14', '2015-09-03'],
  '5.5': ['2015-07-10', '2016-07-21'],
  '5.6': ['2017-01-19', '2018-12-31'],
  '7.0': ['2017-12-03', '2019-01-10'],
  '7.1': ['2018-12-01', '2019-12-01'],
  '7.2': ['2019-11-30', '2020-11-30'],
  '7.3': ['2020-12-06', '2021-12-06'],
  '7.4': ['2021-11-28', '2022-11-28'],
  '8.0': ['2022-11-26', '2023-11-26'],
  '8.1': ['2023-11-25', '2025-12-31'],
  '8.2': ['2024-12-31', '2026-12-31'],
  '8.3': ['2025-12-31', '2027-12-31'],
  '8.4': ['2026-12-31', '2028-12-31'],
  '8.5': ['2027-12-31', '2029-12-31'],
};

export type PhpSupportStatus = 'active' | 'security' | 'eol' | 'unknown';

/** Support status of a PHP version string (e.g. "7.4.33") at a point in time. */
export function phpSupportStatus(version: string, at: Date): PhpSupportStatus {
  const minor = /^(\d+\.\d+)/.exec(version.trim())?.[1];
  const window = minor ? PHP_SUPPORT_WINDOWS[minor] : undefined;
  if (!window) return 'unknown';
  const [activeEnd, securityEnd] = window;
  if (at > new Date(`${securityEnd}T23:59:59Z`)) return 'eol';
  if (at > new Date(`${activeEnd}T23:59:59Z`)) return 'security';
  return 'active';
}

// ── Readiness thresholds & engine support ───────────────────────────

/** "Heavy mail" = at or above either threshold (ADR-0016). */
export const PLESK_HEAVY_MAIL_MAILBOXES = 10;
export const PLESK_HEAVY_MAIL_BYTES = 2 * 1024 ** 3; // 2 GiB

/**
 * Plesk database types with a direct Coolify home. Plesk reports MariaDB
 * as `mysql` (they share the protocol), and both map to Coolify engines.
 */
export const PLESK_MIGRATABLE_DB_ENGINES: readonly string[] = ['mysql', 'postgresql'];
